import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  AlertColor,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Step,
  StepButton,
  Stepper,
  Typography,
} from '@mui/material';
import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import strings from '../../../../../common/Translation/Translate';
import Form, { FormDataModel } from '../../../../../components/Form';
import Notification from '../../../../../components/Notification';
import PageLoader from '../../../../../components/PageLoader';
import {
  ConfigPaymentScheduleSection,
  ConfigPaymentScheduleValues,
  scheduleValuesToVenuePayment,
  validateConfigPaymentSchedule,
  venuePaymentToScheduleValues,
} from 'components/ConfigPaymentScheduleSection';
import axiosInstance from '../../../../../utils/axios';
import { dataFlag, WeekDays } from '../../../../../utils/constants/data';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import {
  createDropdownData,
  fetchIdsFromSelectedDropdownValues,
  ScrollTop,
} from '../../../../../utils/validators/HelperFunctions';
import { removeErrorFieldsFromValues } from '../../../../../utils/validators';
import { fetchFromStorage } from 'utils/storage';
import { pageTitleSx } from 'utils/styles/pageHeader';
import Navigation from '../../../../../utils/hooks/Navigation';
import { CategorizedAddonSelector } from '../CategorizedAddonSelector';
import { AddEditMappingLayout } from '../AddEditVenueConfiguration/Mapping/AddEditMappingLayout/AddEditMappingLayout';
import { AddEditVenueConfigTimeslots } from '../AddEditVenueConfiguration/Mapping/AddEditVenueConfigTimeslots/AddEditVenueConfigTimeslots';
import { AddEditTimeSlotMinimumRevenue } from '../AddEditVenueConfiguration/Mapping/AddEditTimeSlotMinimumRevenue/AddEditTimeSlotMinimumRevenue';
import {
  buildTimeslotMinimumRevenuesPayload,
  buildVenueConfigTimeslotsPayload,
  mapDefaultVenueConfigTimeslotsFromDefaults,
  validateTimeslotMinimumRevenues,
  validateVenueConfigTimeslots,
} from '../AddEditVenueConfiguration/Mapping/AddEditTimeSlotMinimumRevenue/timeslotFormUtils';
import { buildPriceOverridePayload } from '../AddEditVenueConfiguration/Price/priceFormUtils';
import { AddEditPricePackage } from '../AddEditVenueConfiguration/Price/AddEditPricePackage/AddEditPricePackage';
import { AddEditPriceOverRide } from '../AddEditVenueConfiguration/Price/AddEditPriceOverRide/AddEditPriceOverRide';
import { bulkGeneralForm } from './bulkVenueConfigurationForm';
import {
  hourlyPricingSelectOptions,
  isHourlyPricingValue,
  parseBoolFlag,
  toHourlyPricingFormValue,
} from '../AddEditVenueConfiguration/General/hourlyPricingFormUtils';

export type BulkCreateVenueConfigurationLocationState = {
  preselectedVenueIds?: number[];
  preselectedEventTypeIds?: number[];
};

const emptySchedule = (): ConfigPaymentScheduleValues => ({
  minConfirmPaymentPercentage: '',
  paymentSlotOnePercentage: '',
  paymentSlotTwoPercentage: '',
  paymentSlotThreePercentage: '',
  paymentSlotOneBeforeDays: '',
  paymentSlotTwoBeforeDays: '',
  fullPaymentBeforeDays: '',
});

const BulkCreateVenueConfiguration: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const generalFormRef: RefObject<Form | null | undefined> = useRef();

  const [activeStep, setActiveStep] = useState(0);
  const [dropdownData, setDropdownData] = useState<any>({});
  const [generalFormValues, setGeneralFormValues] = useState<any>({});
  const [hourlyPricing, setHourlyPricing] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<number[]>([]);
  const [layouts, setLayouts] = useState<any>([]);
  const [venueConfigTimeslots, setVenueConfigTimeslots] = useState<any[]>([]);
  const [defaultTimeslotRow, setDefaultTimeslotRow] = useState<any>();
  const [revenueRequests, setRevenueRequests] = useState<any[]>([]);
  const [pricePackages, setPricePackages] = useState<any>([]);
  const [priceOverrides, setPriceOverrides] = useState<any>([]);
  const [schedule, setSchedule] = useState<ConfigPaymentScheduleValues>(emptySchedule);
  const [scheduleHasError, setScheduleHasError] = useState(false);
  const [scheduleErrors, setScheduleErrors] = useState<Partial<Record<keyof ConfigPaymentScheduleValues, string>>>(
    {},
  );
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);

  const stepLabels = useMemo(() => {
    const steps = [strings.venueConfig_tabs_general];
    if (!hourlyPricing) {
      steps.push(strings.venueConfig_tabs_price);
    }
    steps.push(strings.venueConfig_tabs_mapping);
    return steps;
  }, [hourlyPricing]);

  const stepCount = stepLabels.length;
  const priceStepIndex = stepLabels.indexOf(strings.venueConfig_tabs_price);
  const mappingStepIndex = stepLabels.indexOf(strings.venueConfig_tabs_mapping);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleScheduleChange = (field: keyof ConfigPaymentScheduleValues, value: unknown) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
    if (scheduleHasError) {
      setScheduleErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const applyDefaults = useCallback(
    (data: any) => {
      setHourlyPricing(parseBoolFlag(data.hourlyPricing));
      setGeneralFormValues({
        available: data.available,
        onlyRFQ: data.onlyRFQ,
        tentativeBookingAllowed: data.tentativeBookingAllowed,
        holdDays: data.holdDays,
        minimumDaysBeforeBooking: data.minimumDaysBeforeBooking ?? data.holdDays,
        preLeadTime: data.preLeadTime ?? 60,
        postLeadTime: data.postLeadTime ?? 60,
        hourlyPricing: toHourlyPricingFormValue(data.hourlyPricing ?? false, strings),
        pricePerHour: data.pricePerHour ?? '',
        bookingConfirmationBy: data.bookingConfirmationBy,
        ...(serviceChargeDropdown
          ? { serviceCharge: data.serviceCharge }
          : { serviceChargePercentage: data.serviceChargePercentage }),
      });
      setSchedule(venuePaymentToScheduleValues(data));
      setScheduleHasError(false);
      setScheduleErrors({});
      const defaultTimeslots = mapDefaultVenueConfigTimeslotsFromDefaults(data);
      setVenueConfigTimeslots(defaultTimeslots);
      setDefaultTimeslotRow(defaultTimeslots[0]);
      setDefaultsLoaded(true);
    },
    [serviceChargeDropdown],
  );

  const loadPageData = useCallback(async () => {
    try {
      setLoading(true);
      const [dropdownRes, defaultsRes] = await Promise.all([
        axiosInstance.get(URLS.venueConfigDropdownData),
        axiosInstance.get(URLS.venueConfigDefaults),
      ]);
      setDropdownData(dropdownRes.data);
      applyDefaults(defaultsRes.data);
    } finally {
      setLoading(false);
    }
  }, [applyDefaults]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    if (!defaultsLoaded || !dropdownData?.venues?.length || !dropdownData?.eventTypes?.length) {
      return;
    }

    const state = location.state as BulkCreateVenueConfigurationLocationState | null;
    if (!state?.preselectedVenueIds?.length && !state?.preselectedEventTypeIds?.length) {
      return;
    }

    setGeneralFormValues((prev: Record<string, unknown>) => {
      const next = { ...prev };

      if (state.preselectedVenueIds?.length) {
        next.venueIds = state.preselectedVenueIds
          .map(id => {
            const venue = dropdownData.venues.find((item: { id: number; name: string }) => item.id === id);
            return venue ? { value: venue.id, label: venue.name } : null;
          })
          .filter(Boolean);
      }

      if (state.preselectedEventTypeIds?.length) {
        next.eventTypeIds = state.preselectedEventTypeIds
          .map(id => {
            const eventType = dropdownData.eventTypes.find(
              (item: { id: number; name: string }) => item.id === id,
            );
            return eventType ? { value: eventType.id, label: eventType.name } : null;
          })
          .filter(Boolean);
      }

      return next;
    });

    navigate(location.pathname, { replace: true, state: null });
  }, [defaultsLoaded, dropdownData, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (hourlyPricing && priceStepIndex >= 0 && activeStep === priceStepIndex) {
      setActiveStep(Math.max(0, priceStepIndex - 1));
    }
  }, [hourlyPricing, activeStep, priceStepIndex]);

  const handleGeneralFormChange = (field: string, value: any) => {
    if (field === 'hourlyPricing') {
      const isHourly = isHourlyPricingValue(value);
      setHourlyPricing(isHourly);
      setGeneralFormValues((prev: any) => ({
        ...prev,
        hourlyPricing: value,
        ...(isHourly
          ? { minimumRevenue: '', fixedHallRental: '', pricePerHour: prev.pricePerHour ?? '' }
          : { minimumRevenue: prev.minimumRevenue, fixedHallRental: prev.fixedHallRental, pricePerHour: '' }),
      }));
    }
  };

  const generalDropdownValue = useMemo(
    () => ({
      available: createDropdownData(dataFlag(strings), ['id', 'name']),
      onlyRFQ: createDropdownData(dataFlag(strings), ['id', 'name']),
      hourlyPricing: hourlyPricingSelectOptions(strings),
      eventTypeIds: createDropdownData(dropdownData.eventTypes, ['id', 'name']),
      tentativeBookingAllowed: createDropdownData(dataFlag(strings), ['id', 'name']),
      venueIds: createDropdownData(dropdownData.venues, ['id', 'name']),
      bookingConfirmationBy: createDropdownData(dropdownData.bookingConfirmationBy, ['id', 'name']),
      serviceCharge: createDropdownData(dropdownData.serviceCharges, ['id', 'name']),
    }),
    [dropdownData],
  );

  const mappingDropdownValue = useMemo(
    () => ({
      addOns: createDropdownData(dropdownData.addOns, ['id', 'name']),
      layouts: createDropdownData(dropdownData.layouts, ['id', 'name']),
      layoutItems: dropdownData.layoutItems ?? [],
    }),
    [dropdownData],
  );

  const priceDropdownValue = useMemo(
    () => ({
      pricePackageId: createDropdownData(dropdownData.pricePackages, ['id', 'name']),
      pricePackage: createDropdownData(dropdownData.pricePackages, ['id', 'name']),
      weekDays: createDropdownData(WeekDays, ['id', 'name']),
    }),
    [dropdownData],
  );

  const toBool = (value: any) => value === true || value === 'true';

  const buildGeneralPayload = (custom: FormDataModel) => ({
    available: toBool(custom.available?.value),
    onlyRFQ: toBool(custom.onlyRFQ?.value),
    tentativeBookingAllowed: toBool(custom.tentativeBookingAllowed?.value),
    maxBookingCapacity: custom.maxBookingCapacity,
    minimumRevenue: isHourlyPricingValue(custom.hourlyPricing) ? null : custom.minimumRevenue,
    minimumDaysBeforeBooking: custom.minimumDaysBeforeBooking,
    fixedHallRental: isHourlyPricingValue(custom.hourlyPricing) ? null : custom.fixedHallRental || null,
    holdDays: custom.holdDays || null,
    preLeadTime: custom.preLeadTime ?? 60,
    postLeadTime: custom.postLeadTime ?? 60,
    hourlyPricing: isHourlyPricingValue(custom.hourlyPricing),
    pricePerHour: isHourlyPricingValue(custom.hourlyPricing)
      ? custom.pricePerHour != null && custom.pricePerHour !== ''
        ? parseFloat(String(custom.pricePerHour))
        : null
      : null,
    unavailabilityDates: [],
  });

  const buildPaymentPayload = (custom: FormDataModel) => {
    const serviceChargePercent = custom.serviceCharge?.value ?? custom.serviceChargePercentage;
    return {
      bookingConfirmationBy: custom.bookingConfirmationBy?.value,
      serviceChargePercentage: serviceChargePercent != null ? parseFloat(String(serviceChargePercent)) : 0,
      serviceCharge: custom.serviceCharge?.value || null,
      ...scheduleValuesToVenuePayment(schedule),
    };
  };

  const buildMappingPayload = () => {
    const timeslotError = validateVenueConfigTimeslots(venueConfigTimeslots);
    if (timeslotError) throw new Error(timeslotError);
    const revenueError = validateTimeslotMinimumRevenues(revenueRequests);
    if (revenueError) throw new Error(revenueError);
    return {
      addOnIds: selectedAddOnIds,
      layouts:
        layouts?.map((item: any) => ({
          ...item,
          layoutId: item.layouts ? item.layouts?.value : item.layoutId,
          layoutName: item.layouts ? item.layouts?.label : item.layoutName,
        })) || [],
      venueConfigTimeslots: buildVenueConfigTimeslotsPayload(venueConfigTimeslots),
      revenueRequests: buildTimeslotMinimumRevenuesPayload(revenueRequests),
    };
  };

  const buildPricePayload = () => ({
    pricePackages:
      pricePackages?.map((item: any) => ({
        ...item,
        pricePackageId: item.pricePackageId,
        pricePackageName: item.pricePackageName,
      })) || [],
    priceOverRides: priceOverrides?.map((item: any, index: number) => buildPriceOverridePayload(item, index)) || [],
  });

  const validateStep = (step: number): boolean => {
    if (step === 0) {
      const result = (generalFormRef.current as Form)?.getFormData();
      if (!result?.isFormValid) return false;
      const body = removeErrorFieldsFromValues(result.formData) as FormDataModel;
      if (
        fetchIdsFromSelectedDropdownValues(body.venueIds).length === 0 ||
        fetchIdsFromSelectedDropdownValues(body.eventTypeIds).length === 0
      ) {
        return false;
      }
      const paymentValidation = validateConfigPaymentSchedule(schedule);
      if (!paymentValidation.valid) {
        setScheduleHasError(true);
        setScheduleErrors(paymentValidation.errors);
        return false;
      }
      setScheduleHasError(false);
      setScheduleErrors({});
      return true;
    }
    if (step === mappingStepIndex) {
      return (
        validateVenueConfigTimeslots(venueConfigTimeslots) === '' &&
        validateTimeslotMinimumRevenues(revenueRequests) === ''
      );
    }
    return true;
  };

  const showStepValidationError = (step: number) => {
    if (step === 0) {
      setMessage({ display: true, severity: 'error', message: strings.requiredText });
    } else if (step === mappingStepIndex) {
      setMessage({ display: true, severity: 'error', message: strings.timeslotRequired });
    }
  };

  const validateCurrentStep = (): boolean => {
    const valid = validateStep(activeStep);
    if (!valid) {
      showStepValidationError(activeStep);
    }
    return valid;
  };

  const validateAllSteps = (): boolean => {
    for (let step = 0; step < stepCount - 1; step++) {
      if (!validateStep(step)) {
        setActiveStep(step);
        showStepValidationError(step);
        return false;
      }
    }
    return true;
  };

  const handleCancel = () => {
    if (submitting) return;
    navigate(routes.venueConfiguration);
  };

  const handleBack = () => {
    if (submitting || activeStep === 0) return;
    setActiveStep(prev => prev - 1);
    ScrollTop(0);
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      ScrollTop(0);
      return;
    }
    setActiveStep(prev => Math.min(prev + 1, stepCount - 1));
    ScrollTop(0);
  };

  const handleStepClick = (step: number) => {
    if (submitting || step === activeStep || step < 0 || step >= stepCount) return;
    setActiveStep(step);
    ScrollTop(0);
  };

  const handleSubmit = async () => {
    if (!validateAllSteps()) {
      ScrollTop(0);
      return;
    }

    const generalResult = (generalFormRef.current as Form)?.getFormData();
    const generalBody = removeErrorFieldsFromValues(generalResult!.formData) as FormDataModel;

    const venueIds = fetchIdsFromSelectedDropdownValues(generalBody.venueIds);
    const eventTypeIds = fetchIdsFromSelectedDropdownValues(generalBody.eventTypeIds);

    try {
      const mapping = buildMappingPayload();
      const request = {
        venueIds,
        eventTypeIds,
        general: buildGeneralPayload(generalBody),
        payment: buildPaymentPayload(generalBody),
        mapping,
        price: isHourlyPricingValue(generalBody.hourlyPricing) ? null : buildPricePayload(),
        localizedAttributes: null,
      };

      setSubmitting(true);
      const { status, data } = await axiosInstance.post(URLS.bulkCreateVenueConfiguration, request);
      if (status === 200 || status === 201) {
        const summary = strings.venueConfig_bulk_create_summary
          .replace('{created}', String(data.createdCount))
          .replace('{skipped}', String(data.skippedCount))
          .replace('{failed}', String(data.failedCount));
        navigate(routes.venueConfiguration, { state: { bulkCreateMessage: summary, bulkCreateSeverity: 'success' } });
      }
    } catch (error: any) {
      ScrollTop(0);
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (activeStep === stepCount - 1) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  const stepPanelSx = (step: number) => ({
    display: activeStep === step ? 'block' : 'none',
  });

  const isLastStep = activeStep === stepCount - 1;

  return (
    <Container className="content p-0" maxWidth={false}>
      {(loading || submitting) && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid container alignItems="center">
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}>{strings.venueConfig_bulk_create_title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {strings.venueConfig_bulk_create_step_hint.replace('{current}', String(activeStep + 1)).replace(
              '{total}',
              String(stepCount),
            )}
          </Typography>
        </Grid>
        <Grid item textAlign="right" xs={2}>
          <Navigation url={routes.venueConfiguration} />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 3 }} />

      <Stepper activeStep={activeStep} nonLinear alternativeLabel sx={{ mb: 3 }}>
        {stepLabels.map((label, index) => (
          <Step key={label}>
            <StepButton onClick={() => handleStepClick(index)} disabled={submitting}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, minHeight: 320 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {stepLabels[activeStep]}
        </Typography>

        <Box sx={stepPanelSx(0)}>
          <Grid container spacing={2}>
            <Form
              key={hourlyPricing ? 'hourly' : 'standard'}
              ref={generalFormRef as RefObject<Form>}
              model={bulkGeneralForm(
                strings,
                hourlyPricing,
                serviceChargeDropdown,
                <ConfigPaymentScheduleSection
                  values={schedule}
                  onChange={handleScheduleChange}
                  hasError={scheduleHasError}
                  errors={scheduleErrors}
                />,
              )}
              values={defaultsLoaded ? generalFormValues : {}}
              dropdownData={generalDropdownValue}
              onChange={handleGeneralFormChange}
            />
          </Grid>
        </Box>

        {priceStepIndex >= 0 && (
          <Box sx={stepPanelSx(priceStepIndex)}>
            <AddEditPricePackage
              editedData={pricePackages}
              setData={setPricePackages}
              dropdownValue={priceDropdownValue}
            />
            <Divider sx={{ my: 2 }} />
            <AddEditPriceOverRide
              editedData={priceOverrides}
              setData={setPriceOverrides}
              dropdownValue={priceDropdownValue}
            />
          </Box>
        )}

        <Box sx={stepPanelSx(mappingStepIndex)}>
          <AddEditVenueConfigTimeslots
            editedData={venueConfigTimeslots}
            setData={setVenueConfigTimeslots}
            defaultTimeslotRow={defaultTimeslotRow}
          />
          <Divider sx={{ my: 2 }} />
          <AddEditTimeSlotMinimumRevenue editedData={revenueRequests} setData={setRevenueRequests} />
          <Divider sx={{ my: 2 }} />
          <AddEditMappingLayout
            editedData={layouts}
            setData={setLayouts}
            dropdownValue={mappingDropdownValue}
            layoutItems={dropdownData.layoutItems ?? []}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {strings.menu_title_addOns}
          </Typography>
          <CategorizedAddonSelector
            addOnItems={dropdownData.addOnItems ?? []}
            selectedIds={selectedAddOnIds}
            onChange={setSelectedAddOnIds}
          />
        </Box>
      </Paper>

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Button onClick={handleCancel} disabled={submitting} sx={{ textTransform: 'capitalize' }}>
            {strings.cancelText}
          </Button>
        </Grid>
        <Grid item>
          <Box display="flex" gap={1}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={submitting}
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: 'capitalize' }}>
                {strings.common_button_back}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handlePrimaryAction}
              disabled={submitting || loading}
              endIcon={isLastStep ? undefined : <ArrowForwardIcon />}
              sx={{ textTransform: 'capitalize', width: 'auto' }}>
              {isLastStep ? strings.venueConfig_bulk_create_submit : strings.next_text}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BulkCreateVenueConfiguration;
